import { useEffect, useMemo, useState } from "react";
import { useGameStore } from "@/store/gameStore";
import { useLegalActions } from "@/hooks/useLegalActions";
import type { EngineAction } from "@/engine/GameEngine";
import Card from "./Card";

type PickupAction = Extract<EngineAction, { type: "pickup" }>;

const PICKUP_TIMEOUT = 3000; // 3 秒默认放弃

export default function PickupPrompt() {
  const state = useGameStore((s) => s.state);
  const humanId = useGameStore((s) => s.humanPlayerId);
  const dispatch = useGameStore((s) => s.dispatch);
  const legalActions = useLegalActions();
  const [remaining, setRemaining] = useState(PICKUP_TIMEOUT);

  const visible = useMemo(() => {
    if (!state || state.phase !== "pickup-response") return false;
    const w = state.pickupWindow;
    if (!w || !w.responders.includes(humanId)) return false;
    return w.decisions[humanId] === "pending";
  }, [state, humanId]);

  // 3 秒倒计时：到点自动放弃
  useEffect(() => {
    if (!visible) return;
    setRemaining(PICKUP_TIMEOUT);
    const startedAt = Date.now();
    const tick = window.setInterval(() => {
      const left = Math.max(0, PICKUP_TIMEOUT - (Date.now() - startedAt));
      setRemaining(left);
    }, 50);
    const auto = window.setTimeout(() => {
      dispatch({ type: "pass-pickup", playerId: humanId });
    }, PICKUP_TIMEOUT);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(auto);
    };
  }, [visible, humanId, dispatch, state?.pickupWindow]);

  const pickupActions = useMemo<PickupAction[]>(
    () => legalActions.filter((a): a is PickupAction => a.type === "pickup"),
    [legalActions]
  );

  if (!state || !visible) return null;
  const w = state.pickupWindow!;
  const victim = state.players.find((p) => p.id === w.victimId);
  const victimName = victim ? victim.hero.name : w.victimId;

  function take(uid: number) {
    const a = pickupActions.find((x) => x.targetUid === uid);
    if (a) dispatch(a);
  }

  function pass() {
    dispatch({ type: "pass-pickup", playerId: humanId });
  }

  const pct = (remaining / PICKUP_TIMEOUT) * 100;

  return (
    <div className="pickup-prompt" role="dialog" aria-modal="true">
      <div className="pickup-prompt__banner">
        <div className="pickup-prompt__title">拾遗机会</div>
        <div className="pickup-prompt__info">
          <span className="pickup-prompt__victim">{victimName}</span>
          <span>因【{w.reason}】弃置了 {w.cards.length} 张牌</span>
        </div>
        <div className="pickup-prompt__hint">
          点击一张牌立即使用【拾遗】取走，不操作则 {Math.ceil(remaining / 1000)} 秒后放弃
        </div>
        <div className="pickup-prompt__timer">
          <div className="pickup-prompt__timer-bar" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="pickup-prompt__cards">
        {w.cards.map((c) => (
          <Card
            key={c.uid}
            card={c}
            compact
            highlight
            onClick={() => take(c.uid)}
          />
        ))}
      </div>
      <div className="pickup-prompt__actions">
        <button className="pickup-prompt__pass" onClick={pass}>
          放弃拾遗
        </button>
      </div>
    </div>
  );
}

// 非阻塞提示条：拾遗窗口中轮到其他玩家决策（此期间人类手牌置灰）
export function PickupWaitingBanner() {
  const state = useGameStore((s) => s.state);
  const humanId = useGameStore((s) => s.humanPlayerId);
  if (!state || state.phase !== "pickup-response") return null;
  const w = state.pickupWindow;
  if (!w) return null;
  if (w.responders.includes(humanId) && w.decisions[humanId] === "pending") {
    return null;
  }
  const pending = w.responders.filter((rid) => w.decisions[rid] === "pending");
  if (pending.length === 0) return null;

  const names = pending.map(
    (rid) => state.players.find((p) => p.id === rid)?.hero.name ?? rid
  );
  return (
    <div className="waiting-banner" role="status">
      <span className="waiting-banner__dot" />
      <span>
        等待【{names.join("】【")}】决定是否使用【拾遗】…
        每位约 1.5 秒，结算前手牌暂不可操作
      </span>
    </div>
  );
}
